/* NRU page flow - v1.0.
   The perceived speed layer. The search drawer opens straight away and fills
   in place, and the rest of the store should feel as considered: the next page
   already on its way before the tap lands, a soft change between pages instead
   of a white flash, photos that resolve rather than snap, and an
   acknowledgement for the pages that really do take a moment.

   Nothing here takes navigation over. This is a set of real pages with app
   blocks on them - the reviews, the Instagram feed, the loyalty widget - and
   every one of those expects a real page load, so every link still navigates
   exactly the way it does today. All that changes is how much of the next page
   is already in the browser by the time it is asked for.

   This file holds the two pieces that need scripting once the page is up:
   warming the next document, and the bar. The crossfade between pages and the
   photo fade are CSS, and live in nru-flow.liquid alongside the few lines that
   claim each photo while the parser is still building the page - that has to
   happen before a picture can paint, which is too early for this file.

   Warming is prefetch and never prerender. A prerendered page runs its own
   scripts, so the analytics and the app embeds would fire for a page the
   shopper may well never open. */
(function () {
  'use strict';

  if (window.__nruFlow) return;
  window.__nruFlow = true;

  /* How many pages one page view may warm. A phone on a shared connection
     should not spend its data on a whole grid of products the shopper has only
     run a thumb past. */
  var WARM_LIMIT = 8;

  /* A page that answers faster than this never shows a bar. Much below a tenth
     of a second a bar reads as noise rather than as an answer. */
  var BAR_DELAY = 150;

  /* A tap the shopper backed out of must not leave the bar on screen for the
     rest of the visit. */
  var BAR_LIFETIME = 12000;

  /* Anything with a side effect, a price attached, or one shopper's own data
     in it. Compared a segment at a time, so /cart covers /cart and /cart/add
     and leaves a collection called /collections/carts alone. */
  var NEVER_WARM = [
    '/cart',
    '/checkout',
    '/account',
    '/tools',
    '/apps',
    '/services',
    '/discount',
    '/localization',
    '/password'
  ];

  /* Links the shop can opt out of by hand, and the ones the browser would not
     treat as a plain page load anyway. */
  var NEVER_WARM_SELECTOR =
    '[download], [data-no-prefetch], [rel~="nofollow"], [target]:not([target="_self"])';

  var here = window.location;

  /* Save-Data is a shopper asking not to be spent; 2g is a connection with
     nothing spare. Either way, warming would take bandwidth away from the page
     they are actually reading. */
  function connectionIsThin() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    return /^(?:slow-)?2g$/.test(c.effectiveType || '');
  }

  /* A market can put a language in front of every path. Take one off before
     comparing, so /en-gb/cart is still the cart. */
  function withoutLocale(path) {
    return path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
  }

  function isPrivate(url) {
    var path = withoutLocale(url.pathname);
    for (var i = 0; i < NEVER_WARM.length; i++) {
      var deny = NEVER_WARM[i];
      if (path === deny || path.slice(0, deny.length + 1) === deny + '/') return true;
    }
    /* Where a shopper is sent the moment they sign in. */
    return url.search.indexOf('logged_in=') > -1;
  }

  /* The document this link would load, or nothing at all if following it is
     not a plain same-origin page load: another site, a download, a new tab, a
     protocol the page cannot navigate to, or the page we are already on. */
  function documentFor(link) {
    if (!link || link.tagName !== 'A') return null;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return null;
    if (link.hasAttribute('download')) return null;
    var target = link.getAttribute('target');
    if (target && target !== '_self') return null;
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return null;
    if (link.protocol !== here.protocol || link.host !== here.host) return null;
    var url;
    try {
      url = new URL(link.href);
    } catch (e) {
      return null;
    }
    /* Only the fragment differs, so the browser stays on this page. */
    if (url.pathname === here.pathname && url.search === here.search) return null;
    return url;
  }

  /* ---- Warming the next page ------------------------------------------- */

  var warmed = {};
  var warmedCount = 0;

  function warm(url) {
    var key = url.href;
    if (warmed[key]) return;
    if (warmedCount >= WARM_LIMIT) return;
    warmed[key] = true;
    warmedCount++;
    var tag = document.createElement('link');
    tag.rel = 'prefetch';
    tag.as = 'document';
    tag.href = key;
    document.head.appendChild(tag);
  }

  function warmLink(link) {
    if (!link || !link.closest) return;
    if (link.closest(NEVER_WARM_SELECTOR)) return;
    var url = documentFor(link);
    if (!url || isPrivate(url)) return;
    warm(url);
  }

  function onPointer(event) {
    var node = event.target;
    if (!node || node.nodeType !== 1 || !node.closest) return;
    warmLink(node.closest('a[href]'));
  }

  function speculationSupported() {
    return !!(
      window.HTMLScriptElement &&
      HTMLScriptElement.supports &&
      HTMLScriptElement.supports('speculationrules')
    );
  }

  /* The same deny list as above, written the way speculation rules want it.
     Built from the one array so the two cannot drift apart. */
  function denyPatterns() {
    var out = [];
    for (var i = 0; i < NEVER_WARM.length; i++) out.push(NEVER_WARM[i] + '*');
    return out;
  }

  /* Moderate eagerness is the browser warming a page once the shopper has
     shown real interest in it - a held hover, a finger going down - rather
     than everything in sight. */
  function addSpeculationRules() {
    var rules = {
      prefetch: [
        {
          where: {
            and: [
              { href_matches: '/*' },
              { not: { href_matches: denyPatterns() } },
              { not: { href_matches: { search: '*logged_in=*' } } },
              { not: { selector_matches: NEVER_WARM_SELECTOR } }
            ]
          },
          eagerness: 'moderate'
        }
      ]
    };
    var script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify(rules);
    document.head.appendChild(script);
  }

  function startWarming() {
    if (connectionIsThin()) return;
    if (speculationSupported()) {
      addSpeculationRules();
      return;
    }
    /* No speculation rules in this browser, so warm by hand as the pointer
       arrives. One link per URL, and only ever a handful of them. */
    document.addEventListener('pointerenter', onPointer, { capture: true });
    document.addEventListener('touchstart', onPointer, { capture: true, passive: true });
  }

  /* ---- The bar --------------------------------------------------------- */

  var bar = null;
  var barTimer = 0;
  var barLife = 0;

  function clearBar() {
    clearTimeout(barTimer);
    clearTimeout(barLife);
    barTimer = 0;
    barLife = 0;
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    bar = null;
  }

  function showBar() {
    barTimer = 0;
    if (bar || !document.body) return;
    bar = document.createElement('div');
    bar.className = 'nru-nav-progress';
    document.body.appendChild(bar);
    /* Commit the empty state before the fill is armed, so the bar grows from
       nothing instead of appearing already part of the way along. */
    void bar.offsetWidth;
    bar.classList.add('nru-nav-progress--on');
    barLife = setTimeout(clearBar, BAR_LIFETIME);
  }

  function expectNavigation() {
    if (bar || barTimer) return;
    barTimer = setTimeout(showBar, BAR_DELAY);
  }

  /* Bubbling, and only for a click nothing else has claimed. The photo swap,
     the drawer and the filters all answer a tap in place and stop it here, so
     none of them ever raises a bar. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var node = event.target;
    if (node && node.nodeType !== 1) node = node.parentElement;
    if (!node || !node.closest) return;
    var link = node.closest('a[href]');
    if (!link || !documentFor(link)) return;
    expectNavigation();
  });

  /* A form that has not been claimed and is only asking for a page - the
     search box, and the filters when their script has not loaded. Anything
     that posts is left alone: on this theme those are all answered in place. */
  document.addEventListener('submit', function (event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var method = (form.getAttribute('method') || 'get').toLowerCase();
    if (method !== 'get') return;
    var target = form.getAttribute('target');
    if (target && target !== '_self') return;
    expectNavigation();
  });

  /* The bar has done its job by the time the next document commits, and a
     page put away with a bar still on it comes back out of the cache wearing
     one. Both ends are covered, because only one of them fires per browser. */
  window.addEventListener('pagehide', clearBar);
  window.addEventListener('popstate', clearBar);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) clearBar();
  });

  startWarming();
})();
